import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  input,
  Output,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeModalButton, SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {SeFeWysiwygModule} from 'se-fe-wysiwyg';
import {Bundle} from '@sportngin/se-commerce-sdk/models/clare';
import {UpdateProductDetail} from '../../../../../../product/components/product-detail/product-detail.store';
import {trimFormValue} from '../../../../../../../utils/form-utils';

interface BundleForm {
  name: FormControl<string>;
  description: FormControl<string | null>;
}

@Component({
  selector: 'bundle-details-modal',
  imports: [
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeIconModule,
    SeFeModalModule,
    SeFeSpinnerModule,
    SeFeWysiwygModule,
  ],
  templateUrl: './bundle-details-modal.component.html',
  styleUrls: ['./bundle-details-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BundleDetailsModalComponent {
  @Output() save = new EventEmitter<UpdateProductDetail<{bundle: Partial<Bundle>}>>();
  @ViewChild('detailModal') detailModal!: SeFeModalComponent;

  error = input<string | null>();
  bundle = input<Bundle>();
  price = input<number>();

  form: FormGroup<BundleForm> = new FormGroup({
    name: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.maxLength(100)]}),
    description: new FormControl('', {
      validators: [Validators.maxLength(2000)],
    }),
  });

  productNameConfig = {
    label: 'Name',
    errorMessages: {
      required: 'Please enter a product name.',
      maxlength: 'Name exceeds maximum length of 100 characters.',
    },
  };

  productDescLabel = {
    label: 'Description',
  };

  detailModalButtons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Cancel',
      action: () => {
        this.detailModal.close();
        return 'Cancel';
      },
      loadingLabel: 'Cancel',
    },
    {
      primary: true,
      label: 'Save',
      action: async () => {
        this.handleAction();
        return 'Saving';
      },
      loadingLabel: 'Updating Product',
    },
  ];

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      this.form.patchValue({
        name: this.bundle()?.name || '',
        description: this.bundle()?.description || '',
      });
      this.cdr.detectChanges();
    });
  }

  get descriptionCtrl(): FormControl<string> {
    return this.form.get('description') as FormControl<string>;
  }

  handleAction(): void {
    trimFormValue(this.form, 'name');
    trimFormValue(this.form, 'description');
    this.markFormGroupControls(this.form);
    this.cdr.markForCheck();
    if (!this.form.valid) {
      this.detailModal?.cancelSpinner?.();
      return;
    }
    this.saveBundle();
  }

  markFormGroupControls(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup) {
        this.markFormGroupControls(control);
      } else {
        control.markAsTouched({onlySelf: true});
        control.markAsDirty({onlySelf: true});
      }
    });
  }

  saveBundle(): void {
    const bundle: Partial<Bundle> = {
      name: this.form.value.name?.trim(),
      description: this.form.value.description || null!,
    };
    this.save.emit({
      data: {bundle},
      modal: this.detailModal,
    });
  }

  resetForm(): void {
    this.form.reset({
      name: this.bundle()?.name,
      description: this.bundle()?.description,
    });
  }
}
